import * as React from 'react'
import { useCallback, useState } from 'react'

import AppBar from '@mui/material/AppBar'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { Theme, useTheme } from '@mui/material/styles'

import { ENTITY_TYPES } from '@sales-center/common'
import {
  ContentCentered,
  SettingsPage,
  TabPanel,
} from '@sales-center/components'

import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

import { FieldSettingsWrapper } from './FieldSettingsWrapper'

/**
 * Theme-derived `sx` objects for this page's app bar, title, and tabs.
 *
 * Scoped to the four values this component reads; the page is the only
 * consumer, so the styles live here rather than in a shared style module. The
 * app bar background and tab panel padding apply under the legacy theme only,
 * and the tab padding and casing differ between the two, so both theme states
 * are resolved here rather than at each usage site.
 *
 * @param theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
const getStyles = (theme: Theme, isTidalThemeActive: boolean) => ({
  appBar: isTidalThemeActive
    ? undefined
    : { backgroundColor: theme.palette.common.white },

  title: {
    display: 'flex',
    alignItems: 'center',
  },

  tab: isTidalThemeActive
    ? { px: 2, whiteSpace: 'nowrap', textTransform: 'capitalize' as const }
    : { padding: `0 ${theme.spacing(4)}`, whiteSpace: 'nowrap' },

  tabPanel: isTidalThemeActive ? undefined : {},
})

/**
 * Route-level container for the Lead/Opportunity field settings page.
 *
 * Renders a tab per entity type and tracks which one is active. It performs no
 * data fetching of its own: each tab's `FieldSettingsWrapper` loads its own
 * settings on mount.
 *
 * `TabPanel` renders only the active tab's children, so exactly one
 * `FieldSettingsWrapper` is mounted at a time. Switching tabs unmounts the
 * previous wrapper and mounts the next one, which is what triggers the fetch
 * for the newly selected entity type.
 *
 * Renders correctly under both Tidal theme states. Every theme-dependent
 * style is resolved through `getStyles`; the only remaining branch on the flag
 * is the page title, which the Tidal layout omits entirely because each tab's
 * wrapper renders its own heading.
 */
export function EntityFormSettingsPage() {
  const isTidalThemeActive = useTidalThemeActive()
  const [activeTab, setActiveTab] = useState(1)

  const onChangeTab = useCallback((_, value: number) => setActiveTab(value), [])

  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)

  return (
    <SettingsPage
      title={
        isTidalThemeActive ? null : (
          <span style={styles.title}>New Lead/Opportunity Field Settings</span>
        )
      }
      headContent={
        <AppBar
          sx={styles.appBar}
          color="default"
          elevation={1}
          position="sticky"
        >
          <Tabs value={activeTab} onChange={onChangeTab}>
            <Tab value={1} label={`Lead Field Settings`} sx={styles.tab} />
            <Tab
              value={2}
              label={`Opportunity Field Settings`}
              sx={styles.tab}
            />
          </Tabs>
        </AppBar>
      }
    >
      <TabPanel tab={activeTab} value={1} sx={styles.tabPanel}>
        <ContentCentered>
          <FieldSettingsWrapper entityType={ENTITY_TYPES.Lead} />
        </ContentCentered>
      </TabPanel>
      <TabPanel tab={activeTab} value={2} sx={styles.tabPanel}>
        <ContentCentered>
          <FieldSettingsWrapper entityType={ENTITY_TYPES.Opportunity} />
        </ContentCentered>
      </TabPanel>
    </SettingsPage>
  )
}

EntityFormSettingsPage.displayName = 'EntityFormSettingsPage'
